import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Save, Zap, Download, Upload } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { BACKEND_BASE_URL } from "../../config/api";
import AdminGuard, { useCanEdit } from "@/components/AdminGuard";

const BASIC = `${BACKEND_BASE_URL}/api/basic`;
const PHASES = [
  { key: "raw_material", label: "Raw Material" },
  { key: "eol", label: "End-of-Life" },
];

export default function BasicMaterialBenchmarks() {
  const [meta, setMeta] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [countries, setCountries] = useState([]);
  const [worldAvg, setWorldAvg] = useState({});
  const [countryData, setCountryData] = useState({});
  const [selectedCountry, setSelectedCountry] = useState("India");
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const canEdit = useCanEdit();

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${BASIC}/import-material-benchmarks`, fd);
      const { world_avg = 0, country = 0 } = res.data.counts || {};
      toast.success(`Imported: ${world_avg} world + ${country} country rows`);
      await loadAll();
    } catch (err) {
      toast.error("Import failed: " + (err.response?.data?.detail || err.message));
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  const loadAll = async () => {
    const [metaRes, matRes, cRes, wRes, cdRes] = await Promise.all([
      axios.get(`${BASIC}/meta`),
      axios.get(`${BASIC}/materials`),
      axios.get(`${BASIC}/countries`),
      axios.get(`${BASIC}/material-benchmarks`),
      axios.get(`${BASIC}/country-material-data`),
    ]);
    setMeta(metaRes.data);
    setMaterials(matRes.data);
    setCountries(cRes.data);
    setWorldAvg(wRes.data || {});
    setCountryData(cdRes.data || {});
    if (cRes.data.length > 0 && !selectedCountry) setSelectedCountry(cRes.data[0].name);
  };

  useEffect(() => { loadAll().catch(() => {}); }, []);

  const updateWorld = (mat, phase, pk, v) => {
    setWorldAvg(prev => ({
      ...prev,
      [mat]: { ...(prev[mat] || {}), [phase]: { ...((prev[mat] || {})[phase] || {}), [pk]: v === "" ? null : Number(v) } }
    }));
  };
  const updateCountry = (mat, phase, pk, v) => {
    setCountryData(prev => ({
      ...prev,
      [selectedCountry]: {
        ...(prev[selectedCountry] || {}),
        [mat]: { ...((prev[selectedCountry] || {})[mat] || {}), [phase]: { ...(((prev[selectedCountry] || {})[mat] || {})[phase] || {}), [pk]: v === "" ? null : Number(v) } }
      }
    }));
  };

  const saveWorld = async (mat, phase) => {
    setSaving(true);
    try {
      await axios.put(`${BASIC}/material-benchmarks`, { material: mat, phase, values: worldAvg[mat]?.[phase] || {}, units: meta.material_units });
      toast.success(`Saved ${mat} - ${phase}`);
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const saveCountry = async (mat, phase) => {
    setSaving(true);
    try {
      await axios.put(`${BASIC}/country-material-data`, { country: selectedCountry, material: mat, phase, values: countryData[selectedCountry]?.[mat]?.[phase] || {}, units: meta.material_units });
      toast.success(`Saved ${selectedCountry}-${mat}-${phase}`);
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  if (!meta) return <div className="flex items-center justify-center h-screen"><Loader2 className="w-8 h-8 animate-spin text-[#43A047]" /></div>;

  const renderTable = (mat, phase, values, type) => (
    <Table>
      <TableHeader><TableRow className="border-[#E0E6E1]">
        <TableHead className="text-[10px] uppercase tracking-wider text-[#617364] font-bold w-[220px]">Parameter</TableHead>
        <TableHead className="text-[10px] uppercase tracking-wider text-[#617364] font-bold">Value</TableHead>
      </TableRow></TableHeader>
      <TableBody>
        {meta.param_keys.map(pk => (
          <TableRow key={pk} className="border-[#E0E6E1]">
            <TableCell className="text-xs text-[#1B291D]">{meta.param_labels[pk]}<span className="text-[10px] text-[#617364] ml-1">({meta.material_units[pk]})</span></TableCell>
            <TableCell>
              <Input type="number" step="any" value={values?.[pk] ?? ""}
                onChange={e => (type === "world" ? updateWorld(mat, phase, pk, e.target.value) : updateCountry(mat, phase, pk, e.target.value))}
                className="border-[#E0E6E1] h-8 text-sm" />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  return (
    <div className="p-8 max-w-5xl" data-testid="basic-material-bm">
      <div className="flex items-center gap-3 mb-2">
        <h1 className="text-4xl tracking-tight font-light text-[#1B291D]" style={{ fontFamily: 'Work Sans' }}>Basic Material Benchmarks</h1>
        <Badge className="bg-[#43A047] text-white text-[10px] tracking-wider"><Zap className="w-3 h-3 mr-1" />BASIC</Badge>
      </div>
      <p className="text-sm text-[#617364] mb-4">Simplified 2 phases per material (Raw Material, EOL). Values collapsed from Advanced 3-phase data during seed.</p>
      <AdminGuard resource="basic material benchmarks" />

      <input type="file" ref={fileRef} accept=".xlsx,.xls" onChange={handleImport} className="hidden" />

      <div className="mb-4 flex items-center gap-3">
        <Button variant="outline" size="sm" onClick={() => window.open(`${BASIC}/template/material-benchmarks`, '_blank')} className="border-[#E0E6E1] text-[#617364]" data-testid="basic-mat-template-btn">
          <Download className="w-3.5 h-3.5 mr-1" /> Download Template
        </Button>
        <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} className="border-[#E0E6E1] text-[#43A047]" data-testid="basic-mat-import-btn">
          <Upload className="w-3.5 h-3.5 mr-1" /> Import Excel
        </Button>
        <span className="text-[11px] text-[#617364]">Excel includes 2 sheets: <strong>World Average</strong> &amp; <strong>Country Data</strong>.</span>
      </div>

      <Tabs defaultValue="world" className="w-full">
        <TabsList className="bg-[#F8F9F8] border border-[#E0E6E1] p-1 mb-4">
          <TabsTrigger value="world" className="data-[state=active]:bg-white data-[state=active]:text-[#43A047]">World Average</TabsTrigger>
          <TabsTrigger value="country" className="data-[state=active]:bg-white data-[state=active]:text-[#43A047]">Country Data</TabsTrigger>
        </TabsList>

        <TabsContent value="world" className="space-y-4">
          {materials.map(m => (
            <Card key={m.name} className="border-[#E0E6E1] bg-white">
              <CardHeader className="pb-3"><CardTitle className="text-base font-medium text-[#1B291D]" style={{ fontFamily: 'Work Sans' }}>{m.name}</CardTitle></CardHeader>
              <CardContent>
                <Tabs defaultValue="raw_material">
                  <TabsList className="mb-3">
                    {PHASES.map(p => <TabsTrigger key={p.key} value={p.key}>{p.label}</TabsTrigger>)}
                  </TabsList>
                  {PHASES.map(p => (
                    <TabsContent key={p.key} value={p.key}>
                      {renderTable(m.name, p.key, worldAvg[m.name]?.[p.key], "world")}
                      <div className="mt-3 flex justify-end">
                        <Button variant="outline" size="sm" onClick={() => saveWorld(m.name, p.key)} disabled={saving || !canEdit} className="border-[#E0E6E1] text-[#43A047] disabled:opacity-50" title={!canEdit ? "Admin access required" : ""}>
                          <Save className="w-3.5 h-3.5 mr-1" /> Save {p.label}
                        </Button>
                      </div>
                    </TabsContent>
                  ))}
                </Tabs>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="country" className="space-y-4">
          <div className="max-w-xs">
            <Select value={selectedCountry} onValueChange={setSelectedCountry}>
              <SelectTrigger className="border-[#E0E6E1]"><SelectValue /></SelectTrigger>
              <SelectContent>{countries.map(c => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {materials.map(m => (
            <Card key={m.name} className="border-[#E0E6E1] bg-white">
              <CardHeader className="pb-3"><CardTitle className="text-base font-medium text-[#1B291D]" style={{ fontFamily: 'Work Sans' }}>{m.name}</CardTitle></CardHeader>
              <CardContent>
                <Tabs defaultValue="raw_material">
                  <TabsList className="mb-3">
                    {PHASES.map(p => <TabsTrigger key={p.key} value={p.key}>{p.label}</TabsTrigger>)}
                  </TabsList>
                  {PHASES.map(p => (
                    <TabsContent key={p.key} value={p.key}>
                      {renderTable(m.name, p.key, countryData[selectedCountry]?.[m.name]?.[p.key], "country")}
                      <div className="mt-3 flex justify-end">
                        <Button variant="outline" size="sm" onClick={() => saveCountry(m.name, p.key)} disabled={saving || !canEdit} className="border-[#E0E6E1] text-[#43A047] disabled:opacity-50" title={!canEdit ? "Admin access required" : ""}>
                          <Save className="w-3.5 h-3.5 mr-1" /> Save {p.label}
                        </Button>
                      </div>
                    </TabsContent>
                  ))}
                </Tabs>
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
